import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, ArrowRight, CheckCircle2 } from 'lucide-react';
import Layout from '@/components/Layout';
import { CONTACT_INFO, ROUTE_PATHS } from '@/lib/index';

const SERVICES = [
  { icon: '📦', title: '普通货运', desc: '工业品、日用品、电子设备等一般货物' },
  { icon: '❄️', title: '温控货运', desc: '生鲜食品、医药品、实验样品' },
  { icon: '🐾', title: '活体动物', desc: '宠物、商业动物，需提前申报' },
  { icon: '🎨', title: '艺术品/贵重物品', desc: '艺术品、精密仪器、高价值货物' },
  { icon: '🏗️', title: '超尺货物', desc: '工程机械、大型设备、整车' },
  { icon: '🚗', title: '整车运输', desc: '丰田陆巡等越野车直飞达累斯萨拉姆' },
];

const ROUTES = [
  { from: 'CAN', to: 'DAR', label: '广州 → 达累斯萨拉姆', days: '一三五', time: '12:45' },
  { from: 'DAR', to: 'CAN', label: '达累斯萨拉姆 → 广州', days: '二四六', time: '回程' },
  { from: 'DAR', to: 'JNB', label: '达累斯萨拉姆 → 约翰内斯堡', days: '每日', time: '区域' },
  { from: 'DAR', to: 'NBO', label: '达累斯萨拉姆 → 内罗毕', days: '每日', time: '区域' },
];

export default function Cargo() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[400px] overflow-hidden">
        <img src="/images/57x42cm(Cargo Freighter)_ydknHi.jpg" alt="Air Tanzania Cargo"
          className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/45 to-secondary/90" />
        <div className="absolute inset-0 flex flex-col justify-end pb-12">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <p className="text-accent text-xs uppercase tracking-widest font-bold mb-2">Air Tanzania Cargo</p>
            <h1 className="text-5xl font-bold text-white mb-1">货运服务</h1>
            <p className="text-white/65">B767-300F全货机 · 最大载重54吨 · 广州直飞非洲</p>
          </div>
        </div>
      </section>

      {/* Core advantages */}
      <section className="py-14 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">中非货运的直接通道</h2>
              <p className="text-muted-foreground leading-relaxed mb-5">
                坦桑尼亚航空专业货机队由波音767-300F全货机构成，最大业载约54吨，
                每周三班往返广州白云国际机场与达累斯萨拉姆朱利叶斯·尼雷尔国际机场，
                是目前中国飞往东非最快、最直接的全货机航线之一。
              </p>
              <ul className="space-y-2.5 mb-6">
                {[
                  '广州直飞，无中转，货物不拆箱',
                  '全货机最大业载约54吨，适合整批出运',
                  '电子纸质双轨提单，全程可追踪',
                  '与DAR码头直接联运，延伸至坦内陆城市',
                  '整车服务已成功运输丰田陆巡等车型',
                ].map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 size={15} className="text-primary mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex gap-3">
                <a href={`mailto:${CONTACT_INFO.agentEmail}`}
                  className="flex items-center gap-2 px-5 py-3 bg-secondary text-white rounded-xl text-sm font-semibold hover:bg-secondary/90 transition">
                  <Package size={14} /> 询价订舱
                </a>
                <a href={`tel:${CONTACT_INFO.phone}`}
                  className="flex items-center gap-2 px-5 py-3 bg-muted border border-border rounded-xl text-sm font-semibold hover:bg-muted/80 transition">
                  📞 电话咨询
                </a>
              </div>
            </div>

            <div className="space-y-3">
              <div className="relative rounded-2xl overflow-hidden h-56">
                <img src="/images/57x42cm(Cargo).jpg" alt="货物装载" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/50 to-transparent" />
                <span className="absolute bottom-3 left-3 text-white text-sm font-semibold">整车陆巡装载</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { v: '54T', l: '最大业载' },
                  { v: '3班/周', l: '广州出发' },
                  { v: '12h45m', l: '飞行时长' },
                ].map(s => (
                  <div key={s.l} className="bg-muted rounded-xl p-4 text-center">
                    <div className="text-xl font-bold text-primary">{s.v}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-12 bg-muted">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-2">货运服务类型</h2>
          <p className="text-muted-foreground text-sm mb-8">覆盖多种货物类型，专业处理一站到底</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SERVICES.map((s, i) => (
              <motion.div key={s.title}
                initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="bg-card border border-border rounded-2xl p-5 hover:shadow-md transition">
                <span className="text-3xl">{s.icon}</span>
                <h3 className="font-semibold mt-2 mb-1 text-sm">{s.title}</h3>
                <p className="text-muted-foreground text-xs">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Route cards */}
      <section className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">主要货运航线</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ROUTES.map(r => (
              <div key={r.label} className="bg-card border border-border rounded-2xl p-5 flex items-center gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold font-mono text-primary">{r.from}</div>
                </div>
                <div className="flex-1 flex flex-col items-center gap-1">
                  <div className="flex items-center gap-1 text-muted-foreground text-xs">
                    <span className="w-12 h-px bg-border" />
                    <Package size={12} />
                    <span className="w-12 h-px bg-border" />
                  </div>
                  <span className="text-xs text-muted-foreground">{r.days}</span>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold font-mono text-primary">{r.to}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-xs text-muted-foreground">{r.label.split('→')[1]?.trim()}</div>
                  <div className="text-xs text-accent font-mono mt-0.5">{r.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 bg-secondary text-white text-center">
        <h2 className="text-2xl font-bold mb-1">立即咨询货运方案</h2>
        <p className="text-white/55 text-sm mb-5">代理专线 · 中文服务 · 工作日即时响应</p>
        <div className="flex justify-center gap-3">
          <a href={`mailto:${CONTACT_INFO.agentEmail}`}
            className="px-7 py-3.5 bg-accent text-accent-foreground rounded-xl font-bold text-sm hover:bg-accent/90 transition">
            发送询价邮件
          </a>
          <Link to={ROUTE_PATHS.CONTACT}
            className="px-7 py-3.5 bg-white/10 text-white rounded-xl font-bold text-sm hover:bg-white/20 transition">
            在线留言
          </Link>
        </div>
      </section>
    </Layout>
  );
}
